import { IoClose } from 'react-icons/io5';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, SearchInput } from '@ds';
import { useAppContext } from '@features/contexts/AppProvider';
import { ChangeEvent, useState } from 'react';
import { showExtendedResourcesInfo } from '@features/components/CaseOverview/components/AssignedResources/utils';
import { useFilters } from '@/hooks/useFilters';
import { useAssignResourcesToCase } from '@/hooks/useAssignResourcesToCase';
import { Case } from '@/api/apis/case';
import { ActiveResourceType, logisticsResourceTypes, resourcesAssignFilters, SurgeonType } from './config';
import { getAssignedResources, getResourcesModalState } from './utils';
import { getTimeZonedDate } from '@/utils/helpers';
import { EmployeeRole } from '@/api/apis/resources';
import { CustomerRole } from '@/api/apis/customer';
import {
  ActiveResourceTable,
  AssignmentActionsFooter,
  CaseResourceFilters,
  GoTimePicker,
  OpenAssignmentCalendarButton,
  OrDateTimePicker,
  ServiceWithoutCoordinatorCheckbox,
  WarningsList,
} from './components';
import {
  useGoTimeState,
  useHubSelection,
  useOrDateTimeState,
  useResourceWarnings,
  useTxUsersAssign,
  WarningType,
} from './hooks';

type AssignResourcesModalProps = {
  onClose: () => void;
  onOpenChange: () => void;
  onSetPrimaryModalOpen: () => void;
  caseInfo: Case;
};

export const AssignResourcesModal = ({
  onClose,
  onOpenChange,
  onSetPrimaryModalOpen,
  caseInfo,
}: AssignResourcesModalProps) => {
  const {
    caseId,
    assignedCustomers = {},
    assignedEmployees = {},
    assignedOpoContacts,
    resources,
    organType,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    txCenterDetails,
  } = caseInfo;

  const orCaseDateTime = getTimeZonedDate(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone);
  const { type } = useAppContext();
  const [searchTerm, setSearchTerm] = useState('');
  const { filters, updateFilters } = useFilters(resourcesAssignFilters);
  const { selectedHub, onHubSelectChange, hubSelectList } = useHubSelection();

  const { assignedContacts, clinicalSpecialistIds, surgeonIds, ocsConsoles, assignedUsersByRoles } =
    getAssignedResources(assignedCustomers, assignedEmployees, assignedOpoContacts, resources);

  const { resourceType, selectedRole, showResourcesList } = getResourcesModalState(filters, !!selectedHub);

  const { orDateTime, showOrTypeSwitch, isCustomOr, setIsCustomOr, setOrTimeCustom } =
    useOrDateTimeState(orCaseDateTime);
  const { applyGoTime, goDateTime, setGoDateTime } = useGoTimeState(orCaseDateTime);

  const { warnings, toggleWarning } = useResourceWarnings();

  const {
    onUserAssign,
    onOpoContactAssign,
    onDeviceReserve,
    onClinicalSpecialistReserve,
    onSurgeonReserve,
    saveAssignment,
    usersToAssign,
    opoContactsToAssign,
    newClinicalSpecialistIds,
    newSurgeonIds,
    newDevicesIds,
    pendingUsers,
    isSubmitting,
    setPendingUsers,
  } = useAssignResourcesToCase({
    selectedRole,
    assignedUsersByRoles,
    assignedContacts,
    ocsConsoles,
    clinicalSpecialistIds,
    surgeonIds,
    caseId,
    useExtendedResourcesInfo: showExtendedResourcesInfo({ appType: type }),
    onSave: onClose,
    onSetPrimaryModalOpen,
    isPrimaryUnassigned: warnings.some((w) => w.type === WarningType.Primary),
  });

  const { hasCoordinator, onTxAssign, serviceWithoutCoordinator, handleServiceWithoutCoordinatorChange } =
    useTxUsersAssign({
      assignedCustomers,
      selectedRole,
      onUserAssign,
      pendingUsers,
      setPendingUsers,
      assignedUsersByRoles,
    });

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const isContractorSurgeon = filters.surgeonType[0] === SurgeonType.Contractor;

  const hasTxCoordinatorsToAssign = pendingUsers[CustomerRole.TxCenterCoordinator]?.assign.length;

  const resourcesSelection = {
    usersToAssign,
    opoContactsToAssign,
    newClinicalSpecialistIds,
    newSurgeonIds,
    newDevicesIds,
    ocsConsoles,
  };

  const assignmentActions = {
    onUserAssign,
    onTxAssign,
    onOpoContactAssign,
    onDeviceReserve,
    onClinicalSpecialistReserve,
    onSurgeonReserve,
  };

  return (
    <Modal
      isOpen
      size="5xl"
      className="max-w-352!"
      onOpenChange={onOpenChange}
      scrollBehavior="inside"
      closeButton={<IoClose size={42} className="z-1000" />}
      classNames={{
        header: 'text-hl! text-base-10 font-secondary flex flex-col gap-1 bg-base-80 rounded-2xl',
        body: 'bg-base-80 scroll',
        footer: 'bg-base-80 rounded-2xl',
      }}
    >
      <ModalContent>
        <ModalHeader>Assign Resources</ModalHeader>
        <ModalBody>
          <div className="flex justify-between">
            <div className="flex flex-wrap items-center gap-10">
              <CaseResourceFilters
                filters={filters}
                updateFilters={updateFilters}
                hubsList={hubSelectList}
                selectedHub={selectedHub}
                onHubSelectChange={onHubSelectChange}
                txCenterId={txCenterDetails?.id}
              />
              {logisticsResourceTypes.includes(resourceType) && (
                <OrDateTimePicker
                  showOrTypeSwitch={showOrTypeSwitch}
                  orDateTime={orDateTime}
                  onOrDateTimeChange={setOrTimeCustom}
                  isCustom={isCustomOr}
                  onIsCustomChange={setIsCustomOr}
                />
              )}
              {resourceType === ActiveResourceType.ClinicalSpecialist && applyGoTime && (
                <GoTimePicker dateTime={goDateTime} setDateTime={setGoDateTime} />
              )}
            </div>
            <OpenAssignmentCalendarButton
              day={orDateTime.toAbsoluteString()}
              employeeRole={selectedRole as EmployeeRole}
              hubId={selectedHub?.id}
              organType={organType}
              isContractor={isContractorSurgeon}
            />
          </div>
          {showResourcesList && <SearchInput placeholder="Search" value={searchTerm} onChange={onSearchChange} />}
          <ActiveResourceTable
            toggleWarning={toggleWarning}
            resourceType={resourceType}
            searchTerm={searchTerm}
            assignmentActions={assignmentActions}
            resourcesSelection={resourcesSelection}
            timeProps={{ orDateTime, goDateTime, applyGoTime }}
            filterProps={{ filters, selectedHub }}
            caseInfo={caseInfo}
          />
          <WarningsList warnings={warnings} />
        </ModalBody>
        <ModalFooter className="flex flex-col gap-6">
          {filters.txCenterContact[0] === CustomerRole.TxCenterCoordinator && (
            <ServiceWithoutCoordinatorCheckbox
              isSelected={serviceWithoutCoordinator && !hasTxCoordinatorsToAssign}
              isDisabled={!hasCoordinator && !hasTxCoordinatorsToAssign}
              onValueChange={handleServiceWithoutCoordinatorChange}
              showInfoBanner={serviceWithoutCoordinator && !hasTxCoordinatorsToAssign}
            />
          )}
          <AssignmentActionsFooter
            onClose={onClose}
            onConfirm={saveAssignment}
            isConfirmDisabled={!showResourcesList}
            isLoading={isSubmitting}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
